import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import EmailIcon from "@mui/icons-material/Email";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { motion } from "framer-motion";
import api from "../../../utils/api";

const WorkOrderTimeline = ({ workOrderId }) => {
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const response = await api.get(`/workOrderTimeline/${workOrderId}`);
        setTimelineData(response.data);
      } catch (error) {
        console.error("Error fetching timeline data:", error);
        setError("Failed to load timeline data.");
      } finally {
        setLoading(false);
      }
    };

    fetchTimeline();
  }, [workOrderId]);

  const combinedTimeline = useMemo(() => {
    if (!timelineData || !timelineData.workOrder) return [];

    const { workOrder, comments = [], events = [], emails = [] } = timelineData;

    // Build opener display (name → username → #id → Unknown)
    const openerDisplay =
      (workOrder.opened_by_name && workOrder.opened_by_name.trim()) ||
      (workOrder.opened_by_username && workOrder.opened_by_username.trim()) ||
      (workOrder.opened_by_user_id != null
        ? `User #${workOrder.opened_by_user_id}`
        : null) ||
      "Unknown";

      const resolverDisplay =
  (workOrder.resolved_by_name && workOrder.resolved_by_name.trim()) ||
  (workOrder.resolved_by_username && workOrder.resolved_by_username.trim()) ||
  (workOrder.resolved_by != null ? `User #${workOrder.resolved_by}` : null) ||
  "Unknown";

    const baseItems = [];

    // "Opened by ..." entry
    baseItems.push({
      type: "opened_by",
      title: `Opened by ${openerDisplay}`,
      time: workOrder.created_at,
    });

    // Comments (with user name/username)
    baseItems.push(
      ...comments.map(({ comment_text, sent_at, user_fullname, username }) => ({
        type: "comment",
        title: `${user_fullname || username || "Unknown User"}: ${comment_text}`,
        time: sent_at || new Date().toISOString(),
      }))
    );

    // Events
    baseItems.push(
      ...events.map(({ title, created_at }) => ({
        type: "event",
        title,
        time: created_at || new Date().toISOString(),
      }))
    );

    // Emails
    baseItems.push(
      ...emails.map(({ subject, sent_at }) => ({
        type: "email",
        title: subject,
        time: sent_at || new Date().toISOString(),
      }))
    );

    // Resolved
  // Resolved (with user name)
if (workOrder.resolved_at) {
  baseItems.push({
    type: "workOrder",
    title: `Resolved by ${resolverDisplay}`,
    time: workOrder.resolved_at,
  });
}

    // Sort by time ASC
    return baseItems.sort((a, b) => new Date(a.time) - new Date(b.time));
  }, [timelineData]);

  if (loading) return <Typography>Loading timeline...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold", color: "#333" }}>
        Work Order Timeline
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          paddingLeft: 3,
          borderLeft: "2px solid #ccc",
        }}
      >
        {combinedTimeline.map(({ type, title, time }, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card
              sx={{
                position: "relative",
                mb: 2,
                padding: 1.5,
                boxShadow: 1,
                borderRadius: 2,
                maxWidth: "80%",
                bgcolor: "#fff",
                border: "1px solid #ddd",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  boxShadow: 3,
                  transform: "scale(1.02)",
                },
              }}
            >
              <CircleIcon
                sx={{
                  color: {
                    opened_by: "#1976d2",
                    workOrder: "#1E88E5",
                    comment: "#43A047",
                    event: "#8E24AA",
                    email: "#FB8C00",
                  }[type] || "#757575",
                  fontSize: 14,
                  position: "absolute",
                  left: "-25px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />

              <CardContent sx={{ padding: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  {type === "opened_by" && (
                    <PersonOutlineIcon sx={{ color: "#1976d2", mr: 1, fontSize: 20 }} />
                  )}
                  {type === "workOrder" && (
                    <AssignmentTurnedInIcon sx={{ color: "#1E88E5", mr: 1, fontSize: 20 }} />
                  )}
                  {type === "comment" && (
                    <ChatBubbleIcon sx={{ color: "#43A047", mr: 1, fontSize: 20 }} />
                  )}
                  {type === "event" && (
                    <EventNoteIcon sx={{ color: "#8E24AA", mr: 1, fontSize: 20 }} />
                  )}
                  {type === "email" && (
                    <EmailIcon sx={{ color: "#FB8C00", mr: 1, fontSize: 20 }} />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: "bold", color: "#333" }}>
                    {title}
                  </Typography>
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    fontStyle: "italic",
                    display: "block",
                    mt: 0.5,
                  }}
                >
                  🕒 {new Date(time).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </Box>

      <Legend />
    </Box>
  );
};

const Legend = () => (
  <Box sx={{ mt: 3 }}>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      {[
        {
          color: "#1976d2",
          icon: <PersonOutlineIcon sx={{ fontSize: 14, mr: 1, color: "#1976d2" }} />,
          label: "Opened By",
        },
        {
          color: "#1E88E5",
          icon: <AssignmentTurnedInIcon sx={{ fontSize: 14, mr: 1, color: "#1E88E5" }} />,
          label: "Work Order Resolved",
        },
        {
          color: "#43A047",
          icon: <ChatBubbleIcon sx={{ fontSize: 14, mr: 1, color: "#43A047" }} />,
          label: "Comments",
        },
        {
          color: "#8E24AA",
          icon: <EventNoteIcon sx={{ fontSize: 14, mr: 1, color: "#8E24AA" }} />,
          label: "Events",
        },
        {
          color: "#FB8C00",
          icon: <EmailIcon sx={{ fontSize: 14, mr: 1, color: "#FB8C00" }} />,
          label: "Email Logs",
        },
      ].map(({ color, icon, label }, index) => (
        <Box key={index} sx={{ display: "flex", alignItems: "center", mr: 3 }}>
          {icon}
          <Typography variant="body2" sx={{ color }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

export default WorkOrderTimeline;
